import React, { useCallback, useEffect, useState } from 'react';
import { Image, Table } from 'react-bootstrap';
import AppModal from '../ui/AppModal';
import { getCandidateById } from '../../services/candidateService';
import StatusUpdatePanel from '../ui/StatusUpdatePanel';
import StatusHistorySection from '../ui/StatusHistorySection';
import { mergeCandidateStatusRemarkUpdate } from '../../utils/statusRemarks';
import { statusRequiresInterviewDate } from '../../utils/candidateStatus';
import SourceProfileDocumentFields from '../ui/SourceProfileDocumentFields';
import { buildApiAssetUrl } from '../../utils/apiAssetUrl';
import {
  formatDisplayDate,
  formatInterviewDate,
  formatLwdDisplay,
  toDateInputValue,
} from '../../utils/dateFormat';
import { getDecryptedData } from '../../utils/authStorage';
import { submitStatusUpdate } from '../../utils/submitStatusUpdate';
import { extractUpdatedCandidateFromStatusResponse, normalizeCandidateRecord } from '../../utils/candidateListState';
import { api } from '../../config/api';
import { CANDIDATE_ACTIVITY_REFRESH_KEYS, useLayout } from '../../layout/AppLayout';

function CandidateProfileModal({ show, onHide, candidateId, onUpdated }) {
  const { triggerTableRefresh } = useLayout();
  const sessionUserType = getDecryptedData('User Type');
  const canUploadCandidateDocuments = ['Admin', 'TeamLead', 'User'].includes(sessionUserType);
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [statusInterviewDate, setStatusInterviewDate] = useState('');
  const [statusRemark, setStatusRemark] = useState('');
  const [savingStatus, setSavingStatus] = useState(false);

  const loadCandidate = useCallback(async () => {
    if (!candidateId) return;
    setLoading(true);
    setLoadError('');
    try {
      const data = normalizeCandidateRecord(await getCandidateById(candidateId));
      setCandidate(data);
      setSelectedStatus('');
      setStatusInterviewDate(toDateInputValue(data?.interviewDate));
      setStatusRemark('');
    } catch (error) {
      console.error('Failed to load candidate:', error);
      setLoadError('Unable to load source profile details.');
      setCandidate(null);
    } finally {
      setLoading(false);
    }
  }, [candidateId]);

  useEffect(() => {
    if (show && candidateId) {
      loadCandidate();
    } else if (!show) {
      setCandidate(null);
      setSelectedStatus('');
      setStatusInterviewDate('');
      setStatusRemark('');
      setLoadError('');
    }
  }, [show, candidateId, loadCandidate]);

  const handleStatusChange = (event) => {
    const nextStatus = event.target.value;
    setSelectedStatus(nextStatus);
    if (!statusRequiresInterviewDate(nextStatus)) {
      setStatusInterviewDate('');
    }
  };

  const handleStatusRemarkAdded = (updatedStatusEntry, updatedCandidate) => {
    if (updatedCandidate) {
      setCandidate(normalizeCandidateRecord(updatedCandidate));
      return;
    }
    if (updatedStatusEntry?._id) {
      setCandidate((prev) => mergeCandidateStatusRemarkUpdate(
        prev,
        updatedStatusEntry._id,
        updatedStatusEntry,
      ));
    }
  };

  const handleUpdateStatus = async () => {
    setSavingStatus(true);
    try {
      await submitStatusUpdate({
        api,
        candidateId,
        selectedStatus,
        statusRemark,
        statusInterviewDate,
        updatedBy: getDecryptedData('Id'),
        onSuccess: async (responseData) => {
          setSelectedStatus('');
          setStatusRemark('');
          triggerTableRefresh(CANDIDATE_ACTIVITY_REFRESH_KEYS);
          const updatedCandidate = extractUpdatedCandidateFromStatusResponse(responseData, candidateId);
          if (updatedCandidate) {
            setCandidate(updatedCandidate);
          } else {
            await loadCandidate();
          }
          onUpdated?.(updatedCandidate || responseData);
        },
      });
    } finally {
      setSavingStatus(false);
    }
  };

  const subtitle = candidate
    ? `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim()
    : '';

  return (
    <AppModal
      show={show}
      onHide={onHide}
      title="Source Profile Details"
      subtitle={subtitle}
      size="xl"
      fullscreen="lg-down"
    >
      {loading && <p className="hr-empty-state">Loading profile...</p>}
      {loadError && <p className="text-danger">{loadError}</p>}

      {!loading && candidate && (
        <div className="table-responsive">
          <Table bordered hover className="hr-table table-sm">
            <tbody>
              <tr>
                <td>
                  {candidate.candidateImage ? (
                    <Image
                      src={buildApiAssetUrl(candidate.candidateImage)}
                      style={{ width: '100px', borderRadius: '100px' }}
                      alt="Candidate"
                    />
                  ) : (
                    '—'
                  )}
                </td>
                <td>
                  <StatusUpdatePanel
                    selectedStatus={selectedStatus}
                    onStatusChange={handleStatusChange}
                    statusRemark={statusRemark}
                    onRemarkChange={(event) => setStatusRemark(event.target.value)}
                    statusInterviewDate={statusInterviewDate}
                    onInterviewDateChange={(event) => setStatusInterviewDate(event.target.value)}
                    onSubmit={handleUpdateStatus}
                    saving={savingStatus}
                  />
                  <hr />
                  <StatusHistorySection
                    candidate={candidate}
                    candidateId={candidateId}
                    onRemarkAdded={handleStatusRemarkAdded}
                    layout="block"
                  />
                </td>
              </tr>
              <tr>
                <td><b>Source Profile Name</b></td>
                <td>{candidate.firstName} {candidate.lastName}</td>
              </tr>
              <tr>
                <td><b>Email</b></td>
                <td>{candidate.email || '—'}</td>
              </tr>
              <tr>
                <td><b>Date of Birth</b></td>
                <td>{formatDisplayDate(candidate.dob)}</td>
              </tr>
              <tr>
                <td><b>Mobile Number</b></td>
                <td>{candidate.mobileNumber || '—'}</td>
              </tr>
              <tr>
                <td><b>Skills</b></td>
                <td>{candidate.candidateSkills || '—'}</td>
              </tr>
              <tr>
                <td><b>CTC</b></td>
                <td>{candidate.ctc || '—'}</td>
              </tr>
              <tr>
                <td><b>ECTC</b></td>
                <td>{candidate.ectc || '—'}</td>
              </tr>
              <tr>
                <td><b>Educational Qualification</b></td>
                <td>{candidate.educationalQualification || '—'}</td>
              </tr>
              <tr>
                <td><b>Total YOE</b></td>
                <td>{candidate.totalYoe || '—'}</td>
              </tr>
              <tr>
                <td><b>Relevant YOE</b></td>
                <td>{candidate.relevantYoe || '—'}</td>
              </tr>
              <tr>
                <td><b>Current Location</b></td>
                <td>{candidate.currentLocation || '—'}</td>
              </tr>
              <tr>
                <td><b>Preferred Location</b></td>
                <td>{candidate.prefLocation || '—'}</td>
              </tr>
              <tr>
                <td><b>Current Org</b></td>
                <td>{candidate.currentOrg || '—'}</td>
              </tr>
              <tr>
                <td><b>Role</b></td>
                <td>{candidate.role || '—'}</td>
              </tr>
              <tr>
                <td><b>Resignation Served</b></td>
                <td>{candidate.resignationServed || '—'}</td>
              </tr>
              <tr>
                <td><b>LWD</b></td>
                <td>{formatLwdDisplay(candidate.lwd)}</td>
              </tr>
              <tr>
                <td><b>Offer In Hand</b></td>
                <td>{candidate.offerInHand || '—'}</td>
              </tr>
              <tr>
                <td><b>Interview Date</b></td>
                <td>{formatInterviewDate(candidate.interviewDate)}</td>
              </tr>
              <tr>
                <td><b>About Source Profile</b></td>
                <td>{candidate.details || '—'}</td>
              </tr>
              <tr>
                <td><b>Recruiter Feedback</b></td>
                <td>{candidate.feedback || '—'}</td>
              </tr>
              <tr>
                <td><b>Remark</b></td>
                <td>{candidate.remark || '—'}</td>
              </tr>
              <SourceProfileDocumentFields
                candidateId={candidate._id}
                updatedResume={candidate.updatedResume}
                ornnovaProfile={candidate.ornnovaProfile}
                canUpload={canUploadCandidateDocuments}
                onDocumentsUpdated={(updated) => {
                  if (!updated) return;
                  setCandidate((prev) => (prev ? { ...prev, ...updated } : prev));
                  onUpdated?.(updated);
                }}
              />
              <tr>
                <td colSpan={2} className="text-center">
                  <strong>Assessment</strong>
                </td>
              </tr>
              {candidate.assessments?.length ? (
                candidate.assessments.map((item, index) => (
                  <React.Fragment key={index}>
                    <tr>
                      <td><b>Assessment</b></td>
                      <td>{item.assessment || '—'}</td>
                    </tr>
                    <tr>
                      <td><b>Expected Rating</b></td>
                      <td>{item.yoe || '—'}</td>
                    </tr>
                    <tr>
                      <td><b>Candidate Rating</b></td>
                      <td>{item.score || '—'}</td>
                    </tr>
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan={2}>No assessments available.</td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      )}
    </AppModal>
  );
}

export default CandidateProfileModal;
